import { useState } from 'react';
import { format } from 'date-fns';
import { TrendingUp, TrendingDown, ArrowUpDown, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { DailyAccuracyResult } from '../../services/forecasts.service';

interface DailyAccuracyTableProps {
  data: DailyAccuracyResult;
}

type SortField = 'date' | 'mape' | 'mae' | 'predictedVolume' | 'actualVolume';
type SortDirection = 'asc' | 'desc';

/**
 * Get accuracy status color based on MAPE threshold
 * Green: <10% error (good)
 * Yellow: 10-20% error (warning)
 * Red: >20% error (poor)
 */
function getAccuracyStatus(mape: number): 'good' | 'warning' | 'poor' {
  if (mape < 10) return 'good';
  if (mape < 20) return 'warning';
  return 'poor';
}

function getStatusIcon(status: 'good' | 'warning' | 'poor') {
  switch (status) {
    case 'good':
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    case 'warning':
      return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
    case 'poor':
      return <XCircle className="w-4 h-4 text-red-600" />;
  }
}

function getStatusBadge(status: 'good' | 'warning' | 'poor') {
  const styles = {
    good: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    poor: 'bg-red-100 text-red-800',
  };

  const labels = {
    good: 'Good',
    warning: 'Warning',
    poor: 'Poor',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {getStatusIcon(status)}
      <span className="ml-1">{labels[status]}</span>
    </span>
  );
}

export function DailyAccuracyTable({ data }: DailyAccuracyTableProps) {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedDaily = [...data.daily].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'date':
        comparison = a.date.localeCompare(b.date);
        break;
      case 'mape':
        comparison = a.mape - b.mape;
        break;
      case 'mae':
        comparison = a.mae - b.mae;
        break;
      case 'predictedVolume':
        comparison = a.predictedVolume - b.predictedVolume;
        break;
      case 'actualVolume':
        comparison = a.actualVolume - b.actualVolume;
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Count days by accuracy status
  const statusCounts = data.daily.reduce(
    (acc, day) => {
      const status = getAccuracyStatus(day.mape);
      acc[status]++;
      return acc;
    },
    { good: 0, warning: 0, poor: 0 }
  );

  const overallStatus = getAccuracyStatus(data.overall.mape);

  return (
    <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
      {/* Summary Cards */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-5 gap-4">
          <div>
            <div className="text-sm text-gray-600">Overall Accuracy</div>
            <div className={`text-2xl font-bold ${
              overallStatus === 'good' ? 'text-green-600' :
              overallStatus === 'warning' ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {data.overall.accuracyScore.toFixed(1)}%
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Overall MAPE</div>
            <div className="text-2xl font-bold text-gray-900">{data.overall.mape.toFixed(1)}%</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Days Tracked</div>
            <div className="text-2xl font-bold text-gray-900">{data.daily.length}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Good Days</div>
            <div className="text-2xl font-bold text-green-600">{statusCounts.good}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Needs Attention</div>
            <div className="text-2xl font-bold text-red-600">{statusCounts.warning + statusCounts.poor}</div>
          </div>
        </div>
      </div>

      {/* Table */}
      {data.daily.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          No accuracy data available. Sync actual values to see daily accuracy breakdown.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center gap-1">
                    Date
                    <ArrowUpDown className="w-4 h-4 text-gray-400" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('predictedVolume')}
                >
                  <div className="flex items-center gap-1">
                    Predicted
                    <ArrowUpDown className="w-4 h-4 text-gray-400" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('actualVolume')}
                >
                  <div className="flex items-center gap-1">
                    Actual
                    <ArrowUpDown className="w-4 h-4 text-gray-400" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Variance
                </th>
                <th
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('mape')}
                >
                  <div className="flex items-center gap-1">
                    MAPE
                    <ArrowUpDown className="w-4 h-4 text-gray-400" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('mae')}
                >
                  <div className="flex items-center gap-1">
                    MAE
                    <ArrowUpDown className="w-4 h-4 text-gray-400" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedDaily.map((day) => {
                const status = getAccuracyStatus(day.mape);
                const variance = day.actualVolume - day.predictedVolume;
                const variancePercent = day.predictedVolume > 0
                  ? ((variance / day.predictedVolume) * 100).toFixed(1)
                  : '0.0';

                return (
                  <tr
                    key={day.date}
                    className={`${
                      status === 'poor' ? 'bg-red-50' :
                      status === 'warning' ? 'bg-yellow-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {format(new Date(day.date), 'EEE, MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {day.predictedVolume.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {day.actualVolume.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-1">
                        {variance >= 0 ? (
                          <TrendingUp className="w-4 h-4 text-green-600" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-600" />
                        )}
                        <span className={variance >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {variance >= 0 ? '+' : ''}{variance.toLocaleString()} ({variancePercent}%)
                        </span>
                      </div>
                    </td>
                    <td className={`px-6 py-4 text-sm font-medium ${
                      status === 'good' ? 'text-green-600' :
                      status === 'warning' ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {day.mape.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {day.mae.toFixed(1)}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(status)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
